import React from 'react';
import {
    Avatar,
    Button,
    Card,
    CircularProgress,
    Container,
    Grid,
    TextField,
    Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { RouteComponentProps } from 'react-router-dom';
import { isEmail, isBlank, getErrorMessage } from '../../utils';
import { Api } from '../../data_sources/api/api';
import { useDocumentShareStyles, DOC_ICON_FG } from './documentShareStyles';

interface DocumentShareRequestRouteParams {
    region: string;
    requestToken: string;
}

interface DocumentShareRequestViewProps
    extends RouteComponentProps<DocumentShareRequestRouteParams> {}

interface AlertState {
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
}

/**
 * Step 2 of the two-step email-gate flow for external compliance document recipients.
 *
 * The recipient lands here after clicking the first link in their invitation email
 * (URL pattern: /:region/document-share/:requestToken/request).
 *
 * They enter their email address; the app calls POST /document-share/{requestToken}/request.
 * If the email matches, the server sends a magic-link email to the recipient.
 *
 * On success, the form hides itself and shows a confirmation message.
 */
const DocumentShareRequestView: React.FC<DocumentShareRequestViewProps> = (props) => {
    const classes = useDocumentShareStyles();
    const { region, requestToken } = props.match.params;

    const [email, setEmail] = React.useState('');
    const [alert, setAlert] = React.useState<AlertState | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [sent, setSent] = React.useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (isBlank(email) || !isEmail(email)) {
            setAlert({
                message: 'Please enter a valid email address.',
                severity: 'error',
            });
            return;
        }

        try {
            setLoading(true);
            setAlert(null);

            const api = new Api(region, '');
            await api.requestDocumentShareLink(requestToken, email.trim());

            setLoading(false);
            setSent(true);
        } catch (err: unknown) {
            setLoading(false);
            setAlert({
                message: getErrorMessage(err) || 'Something went wrong. Please try again.',
                severity: 'error',
            });
        }
    };

    if (sent) {
        return (
            <Container maxWidth="md" className={classes.page}>
                <Grid container className={classes.wrap}>
                    <div className={classes.stack}>
                        <Card variant="outlined" className={classes.hero}>
                            <div className={classes.heroRow}>
                                <Avatar
                                    className={classes.heroAvatar}
                                    style={{ background: '#e7f5e0', color: '#3b6d11' }}
                                >
                                    <CheckCircleIcon />
                                </Avatar>
                                <div className={classes.heroBody}>
                                    <Typography className={classes.eyebrow}>
                                        Secure document
                                    </Typography>
                                    <h1 className={classes.heroTitle}>Check your email</h1>
                                    <Typography className={classes.lead}>
                                        A secure link has been sent to your email address.
                                        Please check your inbox and click the link to view
                                        the document.
                                    </Typography>
                                </div>
                            </div>
                        </Card>
                    </div>
                </Grid>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" className={classes.page}>
            <Grid container className={classes.wrap}>
                <div className={classes.stack}>
                    <Card variant="outlined" className={classes.hero}>
                        <div className={classes.heroRow}>
                            <Avatar className={classes.heroAvatar}>
                                <MailOutlineIcon style={{ color: DOC_ICON_FG }} />
                            </Avatar>
                            <div className={classes.heroBody}>
                                <Typography className={classes.eyebrow}>
                                    Secure document
                                </Typography>
                                <h1 className={classes.heroTitle}>View shared document</h1>
                                <Typography className={classes.lead}>
                                    Enter your email address to receive a secure link to
                                    view the document.
                                </Typography>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} method="POST" style={{ marginTop: 24 }}>
                            {alert && (
                                <Alert
                                    severity={alert.severity}
                                    onClose={() => setAlert(null)}
                                    style={{ marginBottom: 16 }}
                                >
                                    {alert.message}
                                </Alert>
                            )}
                            <TextField
                                label="Email"
                                variant="outlined"
                                fullWidth
                                value={email}
                                color="primary"
                                name="email"
                                id="email"
                                autoFocus
                                data-testid="document-share-email-input"
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter your email address"
                            />
                            <div
                                className={classes.buttonWrapper}
                                style={{ marginTop: 20, display: 'block' }}
                            >
                                <Button
                                    variant="contained"
                                    type="submit"
                                    id="submit"
                                    name="submit"
                                    data-testid="document-share-submit-button"
                                    fullWidth
                                    size="large"
                                    disableElevation
                                    disabled={loading}
                                    className={classes.greenButton}
                                >
                                    Send secure link
                                </Button>
                                {loading && (
                                    <CircularProgress
                                        size={25}
                                        color="primary"
                                        thickness={6.0}
                                        className={classes.buttonProgress}
                                    />
                                )}
                            </div>
                        </form>
                    </Card>
                </div>
            </Grid>
        </Container>
    );
};

export default DocumentShareRequestView;
